import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import ProductDetailSlideIn from "@/components/products/ProductDetailSlideIn";
import { useProductDetails } from "@/hooks/useProductDetails";
import type { ProductDetailContextValue } from "@/interfaces/productDetail";
import { previewDetailPanelState } from "@/lib/productDetailPanel";

/* The product detail drawer for the campaign flow, mounted ONCE here instead of once per step:
   Step 1's gift grid and Step 3's landing preview both open this instance. */

const ProductDetailContext = createContext<ProductDetailContextValue | null>(null);

export function ProductDetailProvider({ children }: { children: ReactNode }) {
  const [productDetail, setProductDetail] = useState<{ id: string; zIndex?: number } | null>(null);
  const openProductDetail = useCallback(
    (id: string, opts?: { zIndex?: number }) => setProductDetail({ id, zIndex: opts?.zIndex }),
    [],
  );
  const closeProductDetail = useCallback(() => setProductDetail(null), []);

  // `?? ""` keeps `useProductDetails` disabled (`enabled: !!productId`) while the panel is closed.
  const productDetailQuery = useProductDetails(productDetail?.id ?? "");

  const { pathname } = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is an intentional trigger the body doesn't read — the panel used to unmount with its step, so leaving a step now has to close it explicitly or a stale product follows the user to the next one.
  useEffect(() => {
    closeProductDetail();
  }, [pathname, closeProductDetail]);

  const value = useMemo<ProductDetailContextValue>(
    () => ({ openProductDetail, closeProductDetail }),
    [openProductDetail, closeProductDetail],
  );

  return (
    <ProductDetailContext.Provider value={value}>
      {children}
      <ProductDetailSlideIn
        {...previewDetailPanelState(productDetail?.id ?? null, productDetailQuery)}
        zIndex={productDetail?.zIndex}
        onClose={closeProductDetail}
        onSendGift={() => {}}
      />
    </ProductDetailContext.Provider>
  );
}

/**
 * The shared panel's open/close handles. Thrown rather than defaulted: a silent no-op would look
 * like a dead gift tile, which is harder to trace than a stack trace naming the missing mount.
 */
export function useProductDetail(): ProductDetailContextValue {
  const context = useContext(ProductDetailContext);
  if (context === null) {
    throw new Error("useProductDetail must be used within a <ProductDetailProvider>");
  }
  return context;
}
